import type {
  Availability,
  Booking,
  Brand,
  CheckAvailabilityRequest,
  ChooseTreatmentRequest,
  ChooseTreatmentResponse,
  CreateBookingRequest,
  CreateBookingResponse,
  Customer,
  CustomerPackage,
  CustomerPackageStatus,
  DeleteTreatmentRequest,
  DeleteTreatmentResponse,
  ListTreatmentsRequest,
  OTPResponse,
  QrPaymentStatus,
  SelectedPackage,
  Store,
  Therapist,
  Treatment,
  ValidateBookingRequest,
  ValidateBookingResponse,
  ValidateCustomerPackagesRequest,
  ValidatePackagesResponse,
} from '@/models';
import { useBookingStore, useBrandStore, useUserStore } from '@/store';

import { apiClient } from './bookingAPI';
import { STORAGE_KEYS } from './storageKeys';

export class BookingService {
  // In-memory cache for countries and lookups
  private static countriesCache:
    | { code: string; name: string; dialCode: string }[]
    | null = null;
  private static countriesMapCache: {
    dialToIso: Record<string, string>;
    isoToDial: Record<string, string>;
  } | null = null;

  /**
   * Load generated countries list from public JSON and cache it.
   * Structure: [{ code: 'TH', name: 'Thailand', dialCode: '66' }, ...]
   */
  static async getCountries(): Promise<
    { code: string; name: string; dialCode: string }[]
  > {
    if (this.countriesCache) return this.countriesCache;
    try {
      const res = await fetch('/generated-countries.json', {
        cache: 'no-cache',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: unknown = await res.json();
      const countries =
        (
          json as {
            countries?: { code: string; name: string; dialCode: string }[];
          }
        ).countries ?? [];
      this.countriesCache = countries;
      return countries;
    } catch (e) {
      console.error('Failed to load generated countries:', e);
      this.countriesCache = [];
      return [];
    }
  }

  static async checkAuthExists(
    telPrefix: string,
    tel: string,
  ): Promise<boolean> {
    return await apiClient.checkAuthExists(telPrefix, tel);
  }

  /**
   * Build and cache lookup maps for countries.
   * Returns dialToIso (e.g. '+66' => 'TH') and isoToDial (e.g. 'TH' => '+66').
   */
  static async getCountryMaps(): Promise<{
    dialToIso: Record<string, string>;
    isoToDial: Record<string, string>;
  }> {
    if (this.countriesMapCache) return this.countriesMapCache;
    const countries = await this.getCountries();
    const dialToIso: Record<string, string> = {};
    const isoToDial: Record<string, string> = {};
    countries.forEach((c) => {
      dialToIso[`+${c.dialCode}`] = c.code;
      isoToDial[c.code] = `+${c.dialCode}`;
    });
    this.countriesMapCache = { dialToIso, isoToDial };
    return this.countriesMapCache;
  }
  /**
   * Get brand information directly from API
   */
  static async getBrandInfo(): Promise<Brand> {
    return apiClient.getBrandInfo();
  }

  /**
   * Set the selected store ID for subsequent API calls
   */
  static setStoreId(storeId: number): void {
    apiClient.setStoreId(storeId);
  }

  /**
   * Get all available stores from brand data
   * Uses brand.branches from the initial brand API call
   */
  static async getStores(): Promise<Store[]> {
    try {
      const brand = await apiClient.getBrandInfo();
      return brand.branches;
    } catch (error) {
      console.error('Failed to fetch stores:', error);
      throw error;
    }
  }

  /**
   * Get store by ID
   */
  static async getStoreById(id: string): Promise<Store | undefined> {
    const stores = await this.getStores();
    return stores.find((store) => store.storeId.toString() === id);
  }

  /**
   * Get available time slots for a specific date and branch
   * Uses POST /availability API
   */
  static async getAvailabilities(
    date: string,
    storeId: string,
    customerCount = 1,
  ): Promise<Availability[]> {
    try {
      // Set the store ID for subsequent API calls
      BookingService.setStoreId(parseInt(storeId));

      const request: CheckAvailabilityRequest = {
        customerCount,
        localDate: date,
      };

      const availabilities = await apiClient.checkAvailability(request);
      return availabilities;
    } catch (error) {
      console.error('Failed to fetch availabilities:', error);
      throw error;
    }
  }
  /**
   * Get all available treatments for a customer
   * Uses GET /list-treatments API
   */
  static async getTreatments(
    customers: Customer[] = [],
    customerIndex = 0,
  ): Promise<Treatment[]> {
    try {
      // Gather authoritative data from the booking store (availabilityId included)
      const { availabilityId } = useBookingStore.getState();

      const request: ListTreatmentsRequest = {
        customers,
        customerIndex,
        availabilityId,
      };

      return await apiClient.listTreatments(request);
    } catch (error) {
      console.error('Failed to fetch treatments:', error);
      throw error;
    }
  }

  /**
   * Add a treatment for a customer
   * Uses POST /treatments/confirm API
   */
  static async addTreatment(
    customerIndex: number,
    treatmentId: number,
    optionId: number,
    isAssignedToAll: boolean,
    partnerCustomerIndex?: number,
  ): Promise<ChooseTreatmentResponse> {
    try {
      // Gather authoritative data from the booking store
      const { customers, availabilityId, selectedPackages } =
        useBookingStore.getState();

      const req: ChooseTreatmentRequest = {
        customers,
        customerIndex,
        treatmentId,
        optionId,
        isAssignedToAll,
        partnerCustomerIndex,
        availabilityId,
        selectedPackages,
      };

      const resp = await apiClient.addTreatment(req);

      // Persist balance summary into the store
      const balance = {
        promotions: resp.promotions ?? [],
        promotionDiscount: resp.promotionDiscount ?? 0,
        packageDiscount: resp.packageDiscount ?? 0,
        subtotal: resp.subtotal ?? 0,
        total: resp.total ?? 0,
        totalDiscount: resp.totalDiscount ?? 0,
        depositTotal: resp.depositTotal ?? 0,
        depositValue: resp.depositValue ?? 0,
        remainingTotal: resp.remainingTotal ?? 0,
      };

      useBookingStore.getState().setBalanceSummary(balance);

      // Sync returned customers with the store if the backend returned them
      useBookingStore.setState({
        customers: resp.customers,
        numberOfGuests: resp.customers.length,
      });

      return resp;
    } catch (error) {
      console.error('Failed to choose treatment:', error);
      throw error;
    }
  }

  /**
   * Reorder from an existing booking history item and persist final state to stores.
   */
  static async reorderBookingFromHistory(
    booking: Booking,
    availabilityId: string,
  ): Promise<ChooseTreatmentResponse> {
    apiClient.setStoreId(booking.storeId);

    let customers: Customer[] = booking.customers.map((customer) => ({
      ...customer,
      therapistPreference: {
        gender: customer.therapistPreference.gender,
        preferredTherapistIds: [
          ...customer.therapistPreference.preferredTherapistIds,
        ],
        unpreferredTherapistIds: [
          ...customer.therapistPreference.unpreferredTherapistIds,
        ],
      },
      treatmentItems: [],
    }));

    let finalBooking!: ChooseTreatmentResponse;

    for (const [customerIndex, customer] of booking.customers.entries()) {
      for (const treatmentItem of customer.treatmentItems) {
        const req: ChooseTreatmentRequest = {
          customers,
          customerIndex,
          treatmentId: treatmentItem.treatmentId,
          optionId: treatmentItem.optionId,
          isAssignedToAll: false,
          partnerCustomerIndex: treatmentItem.partnerCustomerIndex,
          availabilityId,
        };

        finalBooking = await apiClient.addTreatment(req);
        customers = finalBooking.customers;
      }
    }

    let availableTreatments: Treatment[] = [];
    try {
      const listTreatmentsRequest: ListTreatmentsRequest = {
        customers: finalBooking.customers,
        customerIndex: 0,
        availabilityId,
      };
      availableTreatments = await apiClient.listTreatments(
        listTreatmentsRequest,
      );
    } catch (error) {
      console.error(
        'Failed to fetch available treatments after reorder:',
        error,
      );
    }

    useBrandStore.getState().setStoreId(booking.storeId);

    useBookingStore.setState({
      storeId: booking.storeId,
      numberOfGuests: finalBooking.customers.length,
      availabilityId,
      customers: finalBooking.customers,
      availableTreatments,
      name: booking.name ?? '',
      telPrefix: booking.telPrefix ?? '+66',
      tel: booking.tel ?? '',
      email: booking.email ?? '',
      paymentMethod: booking.paymentMethod ?? 0,
      useContactInfoForCustomers: finalBooking.customers.some(
        (customer) => customer.useContactInfo,
      ),
      selectedPackages: [],
      balance: {
        promotions: finalBooking.promotions ?? [],
        promotionDiscount: finalBooking.promotionDiscount ?? 0,
        packageDiscount: finalBooking.packageDiscount ?? 0,
        subtotal: finalBooking.subtotal ?? 0,
        total: finalBooking.total ?? 0,
        totalDiscount: finalBooking.totalDiscount ?? 0,
        depositTotal: finalBooking.depositTotal ?? 0,
        depositValue: finalBooking.depositValue ?? 0,
        remainingTotal: finalBooking.remainingTotal ?? 0,
      },
      bookingCode: '',
    });

    return finalBooking;
  }

  /**
   * Delete a treatment from a customer
   * Uses DELETE /treatments/confirm API
   */
  static async deleteTreatment(
    customerIndex: number,
    treatmentItemIndex: number, // -1 to remove all items from given customer index
  ): Promise<DeleteTreatmentResponse> {
    try {
      // Gather authoritative data from the booking store
      const { customers, availabilityId, selectedPackages } =
        useBookingStore.getState();

      const req: DeleteTreatmentRequest = {
        customers,
        customerIndex,
        treatmentItemIndex,
        availabilityId,
        selectedPackages,
      };

      const resp = await apiClient.deleteTreatment(req);

      // Persist balance summary into the store
      const balance = {
        promotions: resp.promotions ?? [],
        promotionDiscount: resp.promotionDiscount ?? 0,
        packageDiscount: resp.packageDiscount ?? 0,
        subtotal: resp.subtotal ?? 0,
        total: resp.total ?? 0,
        totalDiscount: resp.totalDiscount ?? 0,
        depositTotal: resp.depositTotal ?? 0,
        depositValue: resp.depositValue ?? 0,
        remainingTotal: resp.remainingTotal ?? 0,
      };

      useBookingStore.getState().setBalanceSummary(balance);

      // Sync returned customers with the store if the backend returned them
      useBookingStore.setState({
        customers: resp.customers,
        numberOfGuests: resp.customers.length,
      });

      return resp;
    } catch (error) {
      console.error('Failed to delete treatment:', error);
      throw error;
    }
  }

  /**
   * Validate selected packages with backend
   * Uses POST /customer-packages/validate API
   */
  static async validatePackages(
    selectedPackages: SelectedPackage[],
  ): Promise<ValidatePackagesResponse> {
    try {
      // Gather authoritative data from the booking store
      const { customers } = useBookingStore.getState();

      const selectedPackagesPayload = selectedPackages.map((pkg) => ({
        id: pkg.id,
        value: pkg.value,
      }));

      const request: ValidateCustomerPackagesRequest = {
        selectedPackages: selectedPackagesPayload,
        customers,
      };

      const resp = await apiClient.validateCustomerPackages(request);

      const packageMetaById = new Map(
        selectedPackages.map((pkg) => [pkg.id, pkg]),
      );
      const selectedPackagesWithMeta = resp.selectedPackages.map((pkg) => {
        const meta = packageMetaById.get(pkg.id);
        return {
          ...pkg,
          name: meta?.name,
          type: meta?.type,
          remainingValue: meta?.remainingValue,
          totalValue: meta?.totalValue,
        };
      });

      return {
        ...resp,
        selectedPackages: selectedPackagesWithMeta,
      };
    } catch (error) {
      console.error('Failed to validate packages:', error);
      throw error;
    }
  }

  /**
   * Persist validated package selection and balance summary into booking store.
   */
  static persistValidatedPackages(
    validationResult: ValidatePackagesResponse,
  ): void {
    if (!validationResult.valid) return;

    useBookingStore
      .getState()
      .setSelectedPackages(validationResult.selectedPackages);

    const balance = {
      promotions: validationResult.promotions ?? [],
      promotionDiscount: validationResult.promotionDiscount ?? 0,
      packageDiscount: validationResult.packageDiscount ?? 0,
      subtotal: validationResult.subtotal ?? 0,
      total: validationResult.total ?? 0,
      totalDiscount: validationResult.totalDiscount ?? 0,
      depositTotal: validationResult.depositTotal ?? 0,
      depositValue: validationResult.depositValue ?? 0,
      remainingTotal: validationResult.remainingTotal ?? 0,
    };

    useBookingStore.getState().setBalanceSummary(balance);
  }

  /**
   * Get all available therapists
   * Uses GET /therapists API
   */
  static async getTherapists(): Promise<Therapist[]> {
    try {
      return await apiClient.listTherapists();
    } catch (error) {
      console.error('Failed to fetch therapists:', error);
      throw error;
    }
  }

  /**
   * Validate booking data
   * Uses POST /bookings/validate API
   */
  static async validateBooking(): Promise<ValidateBookingResponse> {
    try {
      // Gather authoritative data from the booking store
      const { customers, availabilityId, selectedPackages } =
        useBookingStore.getState();

      const req: ValidateBookingRequest = {
        customers,
        availabilityId,
        selectedPackages,
      };

      const resp = await apiClient.validateBooking(req);

      // Persist balance summary into the store
      const balance = {
        promotions: resp.promotions ?? [],
        promotionDiscount: resp.promotionDiscount ?? 0,
        packageDiscount: resp.packageDiscount ?? 0,
        subtotal: resp.subtotal ?? 0,
        total: resp.total ?? 0,
        totalDiscount: resp.totalDiscount ?? 0,
        depositTotal: resp.depositTotal ?? 0,
        depositValue: resp.depositValue ?? 0,
        remainingTotal: resp.remainingTotal ?? 0,
      };

      useBookingStore.getState().setBalanceSummary(balance);

      // Sync returned customers with the store if the backend returned them
      useBookingStore.setState({
        customers: resp.customers,
        numberOfGuests: resp.customers.length,
      });

      return resp;
    } catch (error) {
      console.error('Failed to choose treatment:', error);
      throw error;
    }
  }

  /**
   * Submit booking data
   * Uses POST /bookings API
   */
  static async submitBooking(
    booking: CreateBookingRequest,
  ): Promise<CreateBookingResponse> {
    try {
      const bookingPayload: CreateBookingRequest = {
        ...booking,
        selectedPackages: booking.selectedPackages?.map((pkg) => ({
          id: pkg.id,
          value: pkg.value,
        })),
      };

      return await apiClient.createBooking(bookingPayload);
    } catch (error) {
      console.error('Failed to submit booking:', error);
      throw error;
    }
  }

  /**
   * Get booking by booking code
   * Uses GET /bookings API
   */
  static async getBooking(bookingCode: string): Promise<unknown> {
    try {
      return await apiClient.getBooking(bookingCode);
    } catch (error) {
      console.error('Failed to fetch booking:', error);
      throw error;
    }
  }

  /**
   * Get QR payment status for a booking.
   * GET /qr-payments/:bookingCode/status
   */
  // Renamed original implementation so we can easily switch back.
  static async getQrPaymentStatusApi(
    bookingCode: string,
  ): Promise<QrPaymentStatus> {
    try {
      return await apiClient.getQrPaymentStatus(bookingCode);
    } catch (error) {
      console.error('Failed to fetch QR payment status:', error);
      throw error;
    }
  }

  /**
   * TEMP MOCK IMPLEMENTATION (Testing Aid)
   * ------------------------------------------------------------------
   * For local / temporary testing we want the QR payment to auto-complete
   * (status => 'paid') after 20 seconds so flows depending on a successful
   * payment can be exercised without manual backend intervention.
   *
   * Behavior:
   *  - First call per bookingCode stores a start timestamp in-memory.
   *  - Before 20s elapses, it returns the real API status (likely 'unpaid').
   *  - At >= 20s, it returns a synthesized object with status 'paid'.
   *  - No persistence: page reload resets the timer.
   *
   * To restore real behavior just replace usages of getQrPaymentStatus with
   * getQrPaymentStatusApi (or rename this method back) and/or delete this block.
   */
  private static qrStatusStartTimes: Record<string, number> = {};
  private static readonly QR_AUTO_PAY_MS = 20_000; // 20 seconds

  static async getQrPaymentStatus(
    bookingCode: string,
  ): Promise<QrPaymentStatus> {
    // Record start time if first seen
    if (!this.qrStatusStartTimes[bookingCode]) {
      this.qrStatusStartTimes[bookingCode] = Date.now();
    }

    const elapsed = Date.now() - this.qrStatusStartTimes[bookingCode];

    // If elapsed threshold passed, return mocked PAID status
    if (elapsed >= this.QR_AUTO_PAY_MS) {
      try {
        // Try to get real data to preserve expiresAt; fall back if fails
        const real = await this.getQrPaymentStatusApi(bookingCode);
        return { ...real, status: 'paid' };
      } catch {
        // Fallback minimal object if API unreachable
        return {
          bookingCode,
          status: 'paid',
          expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(), // 5 min ahead placeholder
        };
      }
    }

    // Otherwise return real API status
    return await this.getQrPaymentStatusApi(bookingCode);
  }

  /**
   * Check availability for adding a new customer
   * Uses POST /availability API with existing customers data
   */
  static async checkAvailabilityForNewCustomer(
    availabilityId: string,
    customers: Customer[],
  ): Promise<boolean> {
    try {
      const request: CheckAvailabilityRequest = {
        customerCount: customers.length + 1,
        localDate: availabilityId.split('T')[0], // Extract date from ISO string
        availabilityId,
        customers,
      };

      const availabilities = await apiClient.checkAvailability(request);
      return availabilities.some(
        (av) => av.availabilityId === availabilityId && av.available,
      );
    } catch (error) {
      console.error('Failed to check availability for new customer:', error);
      return false;
    }
  }

  // OTP and Package methods
  static async requestOTP(
    destination: string,
    channel?: 'phoneNumber' | 'email',
  ): Promise<OTPResponse> {
    return await apiClient.requestOTP(destination, channel);
  }

  static async validateOTP(
    code: string,
    ref: string,
    phoneNumber?: string,
  ): Promise<boolean> {
    const success = await apiClient.validateOTP(code, ref);
    if (success) {
      try {
        localStorage.setItem(STORAGE_KEYS.OTP_REF, ref);
      } catch (e) {
        console.warn('Failed to persist OTP ref', e);
      }

      if (phoneNumber) {
        try {
          useUserStore.getState().setPhoneNumber(phoneNumber);
        } catch (e) {
          console.warn('Failed to persist verified phone number', e);
        }
      }
    }
    return success;
  }

  /**
   * Returns true if an OTP ref has already been validated and persisted.
   * Currently we only check for presence of the ref. If future backend logic
   * includes expiration beyond initial verification window, we can persist and
   * validate an expiry timestamp here as well.
   */
  static isOTPValidated(): boolean {
    return !!localStorage.getItem(STORAGE_KEYS.OTP_REF);
  }

  static async getCustomerPackages(
    phoneNumber: string,
  ): Promise<CustomerPackage[]> {
    return await apiClient.getCustomerPackages(phoneNumber);
  }

  static async getCustomerPackagesStatus(
    phoneNumber: string,
  ): Promise<CustomerPackageStatus> {
    const ref = localStorage.getItem(STORAGE_KEYS.OTP_REF);
    if (!ref) {
      throw new Error('OTP ref not available');
    }

    return await apiClient.getCustomerPackagesStatus({ phoneNumber, ref });
  }

  /**
   * Mark that a booking has been completed.
   * Sets a flag in localStorage to indicate the booking flow is done.
   * This should be called when the user lands on the Thank You page.
   */
  static markDone(): void {
    try {
      localStorage.setItem(STORAGE_KEYS.BOOKING_DONE, 'true');
      console.log('✅ Booking marked as done');
    } catch (error) {
      console.error('Failed to set booking done flag:', error);
    }
  }

  /**
   * Check if a booking was completed and clear the booking store if needed.
   * This should be called on app initialization (in App.tsx).
   *
   * Clears the store only if:
   * 1. The 'done' flag is set in localStorage
   * 2. The current path is NOT the thank-you page
   *
   * After clearing, removes the 'done' flag.
   */
  static ifDoneThenClearBookingState(): void {
    try {
      const isDone = localStorage.getItem(STORAGE_KEYS.BOOKING_DONE);
      const isOnThankYouPage =
        window.location.pathname.includes('/booking/thank-you');

      if (isDone === 'true' && !isOnThankYouPage) {
        console.log('🧹 Clearing booking store after completed booking');
        const { reset } = useBookingStore.getState();
        reset();
        localStorage.removeItem(STORAGE_KEYS.BOOKING_DONE);
      }
    } catch (error) {
      console.error('Failed to clear booking state:', error);
    }
  }
}
